<template>
  <a-menu v-model:open-keys="openKeys" v-model:selected-keys="selectedKeys" :mode="mode" theme="dark">
    <a-menu-item key="dashboard" @click="toggleCollapsed">
      <router-link to="/">
        <font-awesome-icon icon="fa-home" />
        <span>{{ $t('menu.home') }}</span>
      </router-link>
    </a-menu-item>

    <a-menu-item v-if="canAccess('institutions:institutions')" key="institutions">
      <router-link to="/institutions">
        <font-awesome-icon icon="fa-university" />
        <span>{{ $t('menu.institutions') }}</span>
      </router-link>
    </a-menu-item>

    <a-menu-item v-if="canAccess('department:department')" key="departments">
      <router-link to="/departments">
        <font-awesome-icon icon="fa-university" />
        <span>{{ $t('menu.departments') }}</span>
      </router-link>
    </a-menu-item>

    <a-sub-menu v-if="canAccess('user:read')" key="usersMenu">
      <template #title>
        <font-awesome-icon icon="fa-user" />
        <span>{{ $t('menu.users') }}</span>
      </template>

      <a-menu-item key="users">
        <router-link to="/users">
          <font-awesome-icon icon="fa-user" />
          <span>{{ $t('menu.usersList') }}</span>
        </router-link>
      </a-menu-item>

      <a-menu-item key="nurse_location">
        <router-link to="/nurse-location">
          <font-awesome-icon icon="fa-user" />
          <span>{{ $t('menu.nurseLocation') }}</span>
        </router-link>
      </a-menu-item>
    </a-sub-menu>

    <a-sub-menu v-if="canAccess('procedure:department') || canAccess('category:read')" key="proceduresMenu">
      <template #title>
        <font-awesome-icon icon="fa-tasks" />
        <span>{{ $t('menu.procedures') }}</span>
      </template>

      <a-menu-item v-if="canAccess('procedure:department')" key="procedures" @click="toggleCollapsed">
        <router-link to="/procedures">
          <font-awesome-icon icon="fa-tasks" />
          <span>{{ $t('menu.manageProcedures') }}</span>
        </router-link>
      </a-menu-item>

      <a-menu-item v-if="canAccess('category:read')" key="categories" @click="toggleCollapsed">
        <router-link to="/categories">
          <font-awesome-icon icon="fa-tags" />
          <span>{{ $t('menu.manageCategories') }}</span>
        </router-link>
      </a-menu-item>
    </a-sub-menu>

    <a-sub-menu v-if="canAccess('study:read')" key="study">
      <template #title>
        <font-awesome-icon icon="fa-search" />
        <span>{{ $t('menu.study') }}</span>
      </template>

      <a-menu-item key="study-list" @click="toggleCollapsed">
        <router-link to="/study">
          <font-awesome-icon icon="fa-search" />
          <span>{{ $t('menu.manageStudies') }}</span>
        </router-link>
      </a-menu-item>

      <a-menu-item key="study-statistics" @click="toggleCollapsed">
        <router-link to="/studyresults">
          <font-awesome-icon icon="fa-chart-bar" />
          <span>{{ $t('menu.statistics') }}</span>
        </router-link>
      </a-menu-item>
    </a-sub-menu>

    <a-menu-item v-if="canAccess('learning_object:department')" key="learning-objects">
      <router-link to="/learningobject">
        <font-awesome-icon icon="fa-graduation-cap" />
        <span>{{ $t('menu.learningobject') }}</span>
      </router-link>
    </a-menu-item>

    <a-sub-menu v-if="canAccess('device:read')" key="devices">
      <template #title>
        <font-awesome-icon icon="fa-search" />
        <span>{{ $t('menu.devices') }}</span>
      </template>

      <a-menu-item key="device-charts" @click="toggleCollapsed">
        <router-link to="/device-charts">
          <font-awesome-icon icon="fa-chart-bar" />
          <span>{{ $t('menu.statistics') }}</span>
        </router-link>
      </a-menu-item>

      <a-menu-item key="device-statistics" @click="toggleCollapsed">
        <router-link to="/device-reports">
          <font-awesome-icon icon="fa-chart-bar" />
          <span>{{ $t('menu.reports') }}</span>
        </router-link>
      </a-menu-item>

      <a-menu-item key="device-groups" @click="toggleCollapsed">
        <router-link to="/groups">
          <font-awesome-icon icon="fa-desktop" />
          <span>{{ $t('menu.deviceGroups') }}</span>
        </router-link>
      </a-menu-item>

      <a-menu-item key="device-list" @click="toggleCollapsed">
        <router-link to="/devices">
          <font-awesome-icon icon="fa-desktop" />
          <span>{{ $t('menu.devices') }}</span>
        </router-link>
      </a-menu-item>
    </a-sub-menu>

    <a-menu-item key="patients">
      <router-link to="/patients">
        <font-awesome-icon icon="fa-desktop" />
        <span>{{ $t('menu.patients') }}</span>
      </router-link>
    </a-menu-item>

    <a-menu-item key="visitsList">
      <router-link to="/visits-list">
        <font-awesome-icon icon="fa-file-lines" />
        <span>{{ $t('menu.visitForms') }}</span>
      </router-link>
    </a-menu-item>

    <a-menu-item v-if="canAccess('approval:list') && !hiddenRoutes.includes('approvalRequests')" key="approvalRequests">
      <router-link to="/approval-requests">
        <font-awesome-icon icon="fa-clock" />
        <span>{{ $t('menu.approvalRequests') }}</span>
        <span v-if="hasActionableRequests" class="inline-block w-2 h-2 rounded-full bg-red-500 ml-2 align-middle" />
      </router-link>
    </a-menu-item>

    <a-menu-item v-if="isInstitutionalAdmin" key="billing" @click="openBilling">
      <a href="#">
        <font-awesome-icon icon="fa-credit-card" />
        <span>{{ $t('menu.billing') }} {{ userRoles }}</span>
      </a>
    </a-menu-item>
  </a-menu>
</template>

<script setup>
import { ref, reactive, watch, inject, computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useStore } from '@/store';
import { useRoute } from 'vue-router';
const store = useStore();
const route = useRoute();

const hiddenRoutes = (process.env.VUE_APP_HIDDEN_MAIN_ROUTES ?? 'approvalRequests').split(',');

// props
defineProps({
  toggleCollapsed: Function,
});

async function openBilling() {
  await store.dispatch('subscription/createBillingPortalSession');
}

const mode = ref('inline');

const state = reactive({
  selectedKeys: ['home'],
  openKeys: ['dashboard'],
  preOpenKeys: ['dashboard'],
});

const isInstitutionalAdmin = computed(() => store.getters.currentUserRoles?.includes('institutional_admin'));
const hasActionableRequests = computed(() => store.getters['approvalRequests/hasActionableRequests']);

watch(
  () => route.fullPath,
  () => {
    store.dispatch('approvalRequests/FETCH_ACTIONABLE_COUNT');
  },
  { immediate: true },
);

watch(
  () => state.openKeys,
  (_, oldVal) => {
    state.preOpenKeys = oldVal;
  },
);

const canAccess = inject('canAccess');

const { selectedKeys, openKeys } = state;
</script>

<style>
.ant-layout-sider-children {
  height: fit-content;
}
</style>
